import moment from 'moment';
import { getTotalRequiredSecondsPerDevice } from './date';

/**
 * Format số giây thành chuỗi HH:mm:ss
 * @param {number} totalSeconds
 * @returns {string} 'HH:mm:ss'
 */
export const formatSecondsToHHMMSS = (totalSeconds) => {
  if (
    totalSeconds === null ||
    totalSeconds === undefined ||
    isNaN(totalSeconds) ||
    totalSeconds < 0
  ) {
    return '00:00:00';
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const pad = (num) => String(num).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Lấy mã màu theo phần trăm SLA
 * @param {number} percent
 * @returns {string} mã màu hex
 */
export const getSlaColor = (percent) => {
  if (percent < 80) return '#ef4444'; // Đỏ (Rất thấp)
  if (percent < 95) return '#f59e0b'; // Vàng cam (Cảnh báo)
  return '#0e9347'; // Xanh lá (Tốt)
};

export const getBarColor = getSlaColor;

/**
 * Tính toán các chỉ số SLA cho một Gateway cụ thể
 * @param {Object} gw - Dữ liệu gateway từ API gaps
 * @param {Array} dateRanger - [fromTimestamp, toTimestamp]
 * @param {number} fallbackCameras - Số camera dự phòng khi gateway không có cameraTotal từ API gap
 * @returns {Object} Các chỉ số SLA đã tính toán
 */
export const calculateSingleGatewaySLA = (
  gw,
  dateRanger,
  fallbackCameras = 0,
) => {
  const defaultDateRanger = dateRanger || [
    moment().startOf('day').valueOf(),
    moment().endOf('day').valueOf(),
  ];

  const totalSecondsPerCamera =
    getTotalRequiredSecondsPerDevice(defaultDateRanger);

  if (!gw) {
    const camerasCount = fallbackCameras || 0;
    const totalRequiredSeconds = camerasCount * totalSecondsPerCamera;
    return {
      displayName: 'N/A',
      uploadedPercent: 100,
      uploadedDurationSeconds: totalRequiredSeconds,
      totalUploadS: 0,
      recoverablePercent: 0,
      recoverableOverTotalPercent: 0,
      unrecoverableDurationS: 0,
      unrecoverableRatePercent: 0,
      unrecoverableOverTotalPercent: 0,
      totalGapS: 0,
      camerasCount,
    };
  }

  const camerasCount =
    gw.cameraOnline > 0
      ? gw.cameraOnline
      : gw.cameraTotal > 0
        ? gw.cameraTotal
        : fallbackCameras || 0;
  const totalRequiredSeconds = camerasCount * totalSecondsPerCamera;
  const totalGapS = gw.totalGapS || 0;
  const totalUploadS = gw.totalUploadS || 0;
  const uploadedDurationSeconds = Math.max(0, totalRequiredSeconds - totalGapS);
  const unrecoverableDurationS = Math.max(0, totalGapS - totalUploadS);

  // 1. Tỉ lệ không thể khôi phục (Unrecoverable) tính trên phần Gap
  let unrecoverableRatePercent = 0;
  if (totalGapS > 0) {
    unrecoverableRatePercent = Number(
      ((unrecoverableDurationS / totalGapS) * 100).toFixed(2),
    );
    if (unrecoverableRatePercent > 100) unrecoverableRatePercent = 100;
    if (unrecoverableRatePercent < 0) unrecoverableRatePercent = 0;
  }

  // 2. Tỉ lệ đã tải lên (Uploaded) tính trên Tổng thời gian yêu cầu
  let uploadedPercent = 100;
  if (totalRequiredSeconds > 0) {
    uploadedPercent = Number(
      ((uploadedDurationSeconds / totalRequiredSeconds) * 100).toFixed(2),
    );
    if (uploadedPercent > 100) uploadedPercent = 100;
    if (uploadedPercent < 0) uploadedPercent = 0;
  } else if (totalGapS > 0) {
    uploadedPercent = 0;
  }

  // 3. Tỉ lệ có thể khôi phục (Recoverable) tính trên phần Gap
  let recoverablePercent = 0;
  if (gw.uploadablePercent !== undefined && gw.uploadablePercent !== null) {
    recoverablePercent = Number(Number(gw.uploadablePercent).toFixed(2));
  } else if (totalGapS > 0) {
    recoverablePercent = Number(((totalUploadS / totalGapS) * 100).toFixed(2));
  }
  if (recoverablePercent > 100) recoverablePercent = 100;
  if (recoverablePercent < 0) recoverablePercent = 0;

  // 4. Tỉ lệ tính trên Tổng thời gian yêu cầu (dành cho Tooltip cộng lại đúng 100%)
  let recoverableOverTotalPercent = 0;
  let unrecoverableOverTotalPercent = 0;

  if (totalRequiredSeconds > 0) {
    const recoverableS = Math.min(totalUploadS, totalGapS);
    recoverableOverTotalPercent = Number(
      ((recoverableS / totalRequiredSeconds) * 100).toFixed(2),
    );
    if (recoverableOverTotalPercent > 100) recoverableOverTotalPercent = 100;
    if (recoverableOverTotalPercent < 0) recoverableOverTotalPercent = 0;

    unrecoverableOverTotalPercent = Number(
      Math.max(0, 100 - uploadedPercent - recoverableOverTotalPercent).toFixed(2),
    );
  } else if (totalGapS > 0) {
    const recoverableS = Math.min(totalUploadS, totalGapS);
    recoverableOverTotalPercent = Number(
      ((recoverableS / totalGapS) * 100).toFixed(2),
    );
    unrecoverableOverTotalPercent = Number(
      Math.max(0, 100 - recoverableOverTotalPercent).toFixed(2),
    );
  }

  // 5. Tên hiển thị rút gọn (dành cho biểu đồ BarChart)
  let displayName =
    gw.gatewayName || gw.gatewaySerial || gw.name || gw.serial || 'N/A';
  if (displayName.length > 32) {
    displayName = `${displayName.substring(0, 30)}...`;
  }

  return {
    ...gw,
    displayName,
    camerasCount,
    totalRequiredSeconds,
    uploadedDurationSeconds,
    uploadedPercent,
    totalUploadS,
    recoverablePercent,
    recoverableOverTotalPercent,
    unrecoverableDurationS,
    unrecoverableRatePercent,
    unrecoverableOverTotalPercent,
    totalGapS,
  };
};

/**
 * Tính toán SLA cho danh sách nhiều Gateway (dùng cho báo cáo GatewaySLA)
 * @param {Array} gateways - Danh sách gateway từ API gaps hoặc props
 * @param {Array} dateRanger - [fromTimestamp, toTimestamp]
 * @returns {Array} Danh sách gateway đã tính toán, lọc và sắp xếp theo uploadedPercent tăng dần
 */
export const calculateGatewaysSLA = (gateways, dateRanger) => {
  if (!gateways || !Array.isArray(gateways) || gateways.length === 0) return [];

  const calculated = gateways
    .map((gw) => calculateSingleGatewaySLA(gw, dateRanger))
    .filter((gw) => gw.cameraTotal > 0 || gw.cameraOnline > 0 || gw.totalGapS > 0);

  // Sắp xếp tăng dần theo uploadedPercent (tỉ lệ upload thấp nhất lên đầu cho biểu đồ)
  calculated.sort((a, b) => a.uploadedPercent - b.uploadedPercent);
  return calculated;
};
